/* eslint-disable tailwindcss/no-custom-classname */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/core/utils/cn";
import { fontVariantClasses } from "@repo/core/theme/fonts";

const buttonVariants = cva(
  "group flex items-center justify-center rounded-md web:ring-offset-background web:transition-colors web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-ring web:focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-primary-700 hover:bg-primary-600 active:bg-primary-600",
        destructive: "bg-destructive active:opacity-90 web:hover:opacity-90",
        outline:
          "border border-input bg-background active:bg-accent web:hover:bg-accent web:hover:text-accent-foreground",
        secondary: "bg-secondary active:opacity-80 web:hover:opacity-80",
        ghost:
          "active:bg-accent web:hover:bg-accent web:hover:text-accent-foreground",
        link: "web:underline-offset-4 web:hover:underline web:focus:underline "
      },
      size: {
        default: "native:h-12 native:px-5 native:py-3 h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "native:h-14 h-11 rounded-md px-8",
        icon: "native:h-12 px-4 py-2"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
const buttonTextVariants = cva(
  cn(
    fontVariantClasses.variant1.medium,
    "native:text-base text-sm text-foreground web:whitespace-nowrap web:transition-colors"
  ),
  {
    variants: {
      variant: {
        default: "text-white",
        destructive: "text-destructive-foreground",
        outline: "group-active:text-accent-foreground",
        secondary:
          "text-secondary-foreground group-active:text-secondary-foreground",
        ghost: "group-active:text-accent-foreground",
        link: "text-muted group-active:underline"
      },
      size: {
        default: "",
        sm: "",
        lg: "native:text-lg",
        icon: ""
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          buttonTextVariants({ variant, size, className })
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
